import { useEffect, useState } from "react";
import { vehicleService } from "@/services/api";

/**
 * Street address for a vehicle's current position.
 *
 * The backend resolves this through a billed Google request (Redis-cached), so
 * this hook is careful about *when* it asks:
 *
 *  - Several components on one screen want the same address, so resolved
 *    values and in-flight promises are shared in module scope. Four callers
 *    for one vehicle make one request.
 *
 *  - It does not follow a moving vehicle. Coordinates change faster than any
 *    cache granularity, so polling a driving vehicle is a guaranteed cache
 *    miss every few seconds — real money, for an address that is stale before
 *    it renders. While `moving` is true the last resolved address stays on
 *    screen; when the vehicle stops, one final lookup pins where it parked.
 *    A vehicle that is moving and has no address yet still gets one lookup,
 *    so the field is never permanently blank.
 *
 * Returns { address, resolving }. `resolving` exists so a caller can show
 * "Resolving address…" instead of flashing "unavailable" for the moment before
 * the answer arrives — a missing address is a display detail, not an error
 * worth surfacing.
 */

// ~110 m. The key only needs to be coarse enough that a parked vehicle's GPS
// jitter does not look like movement.
const KEY_PRECISION = 3;

const resolved = new Map(); // "lat,lng" -> address | null
const inflight = new Map(); // "lat,lng" -> Promise

function positionKey(lat, lng) {
  if (lat == null || lng == null) return null;
  return `${lat.toFixed(KEY_PRECISION)},${lng.toFixed(KEY_PRECISION)}`;
}

export function useVehicleAddress(vehicleId, lat, lng, { moving = false } = {}) {
  const key = positionKey(lat, lng);
  const [address, setAddress] = useState(() => (key ? resolved.get(key) ?? null : null));
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (!vehicleId || !key) return;

    if (resolved.has(key)) {
      setAddress(resolved.get(key));
      setResolving(false);
      return;
    }
    // Already showing something for an earlier position and still driving:
    // keep it rather than paying for a lookup that will be wrong shortly.
    if (moving && address) return;

    let alive = true;
    setResolving(true);
    let request = inflight.get(key);
    if (!request) {
      request = vehicleService
        .address(vehicleId)
        .then((value) => {
          resolved.set(key, value);
          return value;
        })
        .catch(() => null)
        .finally(() => inflight.delete(key));
      inflight.set(key, request);
    }
    request.then((value) => {
      if (!alive) return;
      setAddress(value ?? null);
      setResolving(false);
    });

    return () => {
      alive = false;
    };
    // `address` is read above but intentionally not a dependency: re-running
    // this effect because the address arrived would be a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, key, moving]);

  return { address, resolving };
}
